import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { WidgetHeader } from './WidgetCard';
import { Card } from '@shared/ui/containers/Card';
import { widgetTitle, type WidgetProps } from './registry';
import { useScopedUtilityLinks, type ScopedUtilityLink } from '@features/utilities/useScopedUtilityLinks';
import { useUsageActionMeta } from '@features/utilities/useUtilityLinkActions';
import { useUtilityLinkUsageFromMeta } from '@features/utilities/useUtilityLinkUsage';
import { getUtilityPresentation, utilityIconBg } from '@features/utilities/utilityPresentation';
import {
  bucketDailyDeltas,
  formatUsageTotal,
  splitWeekTotals,
  weekOverWeekDeltaPct,
} from '@features/utilities/utilityUsageMath';
import { MarqueeText } from '@shared/ui/primitives/MarqueeText';

/**
 * Home widget: one card with a row per scoped utility link (active property +
 * approved claims). Timeframes driven by each link's get_usage parameters_schema.
 */
export function UtilitiesWidget({ config }: WidgetProps): JSX.Element | null {
  const navigate = useNavigate();
  const { scopedLinks, isLoading, isEmpty } = useScopedUtilityLinks();

  if (!isLoading && isEmpty) {
    return null;
  }

  return (
    <section className="flex flex-col gap-2.5">
      <WidgetHeader title={widgetTitle(config)} />
      <Card noGrain className="overflow-hidden p-0">
        {isLoading ? (
          <Skeleton className="m-4 h-10 rounded-lg" />
        ) : (
          <ul className="divide-y divide-hairline">
            {scopedLinks.map((link) => (
              <UtilityMeterRow
                key={link.link_id}
                link={link}
                onClick={() => navigate(`/app/utilities/${link.link_id}`)}
              />
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function usageReferenceMs(iso?: string | null): number {
  if (!iso) return Date.now();
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : Date.now();
}

function UtilityMeterRow({
  link,
  onClick,
}: {
  link: ScopedUtilityLink;
  onClick: () => void;
}): JSX.Element | null {
  const { meta, isMetaReady } = useUsageActionMeta(link.link_id);
  // Visual identity comes from the handler-advertised kind/overrides (dynamic),
  // falling back to the name heuristic while meta loads.
  const presentation = getUtilityPresentation(link.utilityName, {
    utilityKind: meta?.utilityKind,
    chartKind: meta?.chartKind,
    color: meta?.presentationColor,
  });
  const Icon = presentation.icon;
  const { usagePrimary, usageComparison, isLoading } = useUtilityLinkUsageFromMeta(link.link_id, meta, {
    enabled: isMetaReady,
  });

  const { total, deltaPct, chartValues, summaryLabel } = useMemo(() => {
    const seriesPrimary = usagePrimary.data?.series ?? [];
    const seriesComparison = usageComparison.data?.series ?? [];
    const register =
      usagePrimary.data?.primary_register ??
      meta?.primaryRegisterCode ??
      usagePrimary.data?.registers[0];
    // Home widget is the handler-instructed "simple" display → force current week (7d).
    const dayCount = meta?.homeWidget.simple ? 7 : (meta?.homeWidget.chartDayCount ?? 7);
    const referenceMs = usageReferenceMs(usagePrimary.data?.to ?? usageComparison.data?.to);
    const buckets = bucketDailyDeltas(seriesPrimary, register, dayCount, referenceMs);
    const chartVals = buckets.map((b) => b.value);
    // Keep the headline total, chart, and trend consistent: the current-week
    // figure is the sum of the same daily buckets the chart renders, and the
    // trend compares it to the prior week from the comparison series.
    const currentWeek = chartVals.reduce((sum, v) => sum + v, 0);
    const { priorWeek } = splitWeekTotals(seriesComparison, register, referenceMs);
    const totalValue = usagePrimary.data?.total_consumption ?? currentWeek;
    return {
      total: formatUsageTotal(totalValue, usagePrimary.data?.unit),
      deltaPct: weekOverWeekDeltaPct(currentWeek, priorWeek),
      chartValues: chartVals,
      summaryLabel: meta?.homeWidget.summaryLabel ?? 'This week',
    };
  }, [usagePrimary.data, usageComparison.data, meta]);

  if (isMetaReady && !meta) {
    return null;
  }

  const showLoading = !isMetaReady || isLoading;
  // A meter's home timeframe can be longer than a week; the strip only fits seven bars.
  const week = chartValues.slice(-7);
  const peak = Math.max(...week, 0);
  const trend = trendText(deltaPct);

  return (
    <li>
      <button type="button" onClick={onClick} className="flex w-full items-center gap-3 px-4 py-[0.8125rem] text-left active:opacity-80">
        <span
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill"
          style={{ backgroundColor: utilityIconBg(presentation.color) }}
        >
          <Icon className="h-[1.125rem] w-[1.125rem]" strokeWidth={2} style={{ color: presentation.color }} />
        </span>

        <span className="min-w-0 flex-1">
          <MarqueeText className="text-[0.9375rem] font-semibold text-ink">{link.utilityName}</MarqueeText>
          <MarqueeText className="text-[0.8125rem] text-ink-secondary">{summaryLabel}</MarqueeText>
        </span>

        {showLoading ? (
          <Skeleton className="h-6 w-28 shrink-0" />
        ) : (
          <>
            {/* Mini bar per day for the last seven days, scaled to their peak. */}
            <span aria-hidden className="flex h-6 w-14 shrink-0 items-end gap-[3px]">
              {week.map((value, i) => (
                <span
                  key={i}
                  className="flex-1 rounded-[2px] opacity-80"
                  style={{
                    height: `${peak > 0 ? Math.max(8, (value / peak) * 100) : 8}%`,
                    backgroundColor: presentation.color,
                  }}
                />
              ))}
            </span>

            <span className="flex min-w-16 shrink-0 flex-col items-end">
              <span className="text-[0.9375rem] font-bold text-ink">
                {total.amount}
                {total.unit ? <span className="text-caption font-medium text-ink-muted"> {total.unit}</span> : null}
              </span>
              {trend ? (
                <span
                  className="text-caption font-semibold"
                  style={{ color: deltaPct! < 0 ? 'var(--success)' : deltaPct! > 0 ? 'var(--danger)' : 'var(--ink-muted)' }}
                >
                  {trend}
                </span>
              ) : null}
            </span>
          </>
        )}
      </button>
    </li>
  );
}

/** Week-over-week change in plain words: "6% less", "4% more"; empty when there is no prior week. */
export function trendText(deltaPct: number | null): string {
  if (deltaPct === null) return '';
  if (deltaPct === 0) return 'Same';
  return `${Math.abs(deltaPct)}% ${deltaPct < 0 ? 'less' : 'more'}`;
}
